import { useEffect, useState } from "react";

import { useConsultationNote } from "features/consultations/hooks/useConsultationNote";
import { useUpdateConsultationNotes } from "features/consultations/hooks/useUpdateConsultationNotes";

export const useNoteEditor = ({
  consultationId,
  patientId,
  onAudioLoad,
  onAudioClear,
  onNewNoteSaved,
}) => {
  const [note, setNote] = useState("");
  const [showSave, setShowSave] = useState(false);
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const { data: consultationNotes } = useConsultationNote(consultationId);

  // -1 signals "create new note" to the API.
  // Used when explicitly creating a new note OR on initial load with no existing notes.
  const existingNoteId = currentNoteId ?? consultationNotes?.at(0)?.id;
  const activeNoteId = isCreatingNew || !existingNoteId ? undefined : existingNoteId;

  const { updateNotes, saveNow } = useUpdateConsultationNotes(
    consultationId,
    patientId,
    activeNoteId,
    showSave,
    (savedNote, wasCreating) => {
      if (wasCreating && savedNote?.id) {
        setCurrentNoteId(savedNote.id);
        setIsCreatingNew(false);
        onNewNoteSaved?.(savedNote);
      }
    }
  );

  // Reset editor when consultation changes
  useEffect(() => {
    setNote("");
    setCurrentNoteId(null);
    setIsCreatingNew(false);
  }, [consultationId]);

  // Load audio for first note when available
  useEffect(() => {
    if (consultationNotes?.at(0)?.audioFilePath && consultationNotes?.at(0)?.id) {
      onAudioLoad?.(consultationNotes.at(0).id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationNotes]);

  const handleEditNote = (noteItem, externalCallback) => {
    setNote(noteItem.note || "");
    setCurrentNoteId(noteItem.id);
    setIsCreatingNew(false);
    if (noteItem.audioFilePath) {
      onAudioLoad?.(noteItem.id);
    } else {
      onAudioClear?.();
    }
    externalCallback?.(noteItem);
  };

  const handleNewNote = (externalCallback, skipSave = false) => {
    if (!skipSave && note && activeNoteId) {
      saveNow(note);
    }
    setNote("");
    setCurrentNoteId(null);
    setIsCreatingNew(true);
    onAudioClear?.();
    externalCallback?.(null);
  };

  return {
    note,
    setNote,
    showSave,
    setShowSave,
    currentNoteId,
    consultationNotes,
    updateNotes,
    saveNow,
    handleEditNote,
    handleNewNote,
  };
};
